import { EditNote } from '@mui/icons-material';
import { styled, InputBase, MenuItem, InputAdornment, IconButton } from '@mui/material';
import { unstable_composeClasses as composeClasses, unstable_useEnhancedEffect as useEnhancedEffect } from '@mui/utils';
import {
  DataGridProProps,
  getDataGridUtilityClass,
  GridEditInputCellProps,
  useGridApiContext,
  useGridRootProps,
} from '@mui/x-data-grid-pro';
import { SUPPORTED_LANGUAGE_CODES, SupportedLanguageCode } from '@procureai/schemas';
import { useLanguageCode } from '@procureai/ui-mui';
import { forwardRef, useRef, useState, useEffect, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { I18nEditDialog } from '../../components/I18nEditDialog/I18nEditDialog';
import { AdornmentSelect } from '~/components/AdornmentSelect/AdornmentSelect';

type OwnerState = DataGridProProps;

const useUtilityClasses = (ownerState: OwnerState) => {
  const { classes } = ownerState;
  const slots = {
    root: ['editInputCell'],
  };
  return composeClasses(slots, getDataGridUtilityClass, classes);
};

const GridEditInputCellRoot = styled(InputBase, {
  name: 'MuiDataGrid',
  slot: 'EditInputCell',
  overridesResolver: (_props, styles) => styles.editInputCell,
})<{ ownerState: OwnerState }>(({ theme }) => ({
  ...theme.typography.body2,
  padding: '1px 0',
  '& input': {
    padding: '0 16px',
    height: '100%',
  },
}));

export interface I18nEditCellProps extends GridEditInputCellProps {
  multiline?: boolean;
}

export const I18nEditCell = forwardRef<HTMLInputElement, I18nEditCellProps>((props, ref) => {
  const rootProps = useGridRootProps();

  const {
    id,
    value,
    formattedValue,
    api,
    field,
    row,
    rowNode,
    colDef,
    cellMode,
    isEditable,
    tabIndex,
    hasFocus,
    isValidating,
    debounceMs = 200,
    isProcessingProps,
    onValueChange,
    multiline,
    ...other
  } = props;

  const apiRef = useGridApiContext();
  const inputRef = useRef<HTMLInputElement>(undefined);
  const [valueState, setValueState] = useState(value);
  const classes = useUtilityClasses(rootProps);

  const { languageCode } = useLanguageCode();
  const { t } = useTranslation(undefined, { keyPrefix: 'language' });
  const [selected, setSelected] = useState<SupportedLanguageCode>(languageCode);

  const [modalOpen, setModalOpen] = useState(false);

  const handleChange = async (event: ChangeEvent<HTMLInputElement>, newValue: any) => {
    if (onValueChange) {
      await onValueChange(event, newValue);
    }

    const column = apiRef.current.getColumn(field);

    let parsedValue = newValue;
    if (column.valueParser) {
      parsedValue = column.valueParser(newValue, row, column, apiRef);
    }

    setValueState(parsedValue);
    apiRef.current.setEditCellValue({ id, field, value: parsedValue, debounceMs, unstable_skipValueParser: true });
  };

  const meta = apiRef.current.unstable_getEditCellMeta ? apiRef.current.unstable_getEditCellMeta(id, field) : {};

  useEffect(() => {
    if (meta?.changeReason !== 'debouncedSetEditCellValue') {
      setValueState(value);
    }
  }, [meta?.changeReason, value]);

  useEnhancedEffect(() => {
    if (hasFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [hasFocus]);

  return (
    <>
      <GridEditInputCellRoot
        ref={ref}
        inputRef={inputRef}
        className={classes.root}
        ownerState={rootProps}
        fullWidth
        type={colDef.type === 'number' ? colDef.type : 'text'}
        value={(valueState[selected.toUpperCase()] as string) ?? ''}
        onChange={(event) => {
          const inputValue = event.target.value;
          const newValue = { ...valueState, [selected.toUpperCase()]: inputValue };
          handleChange(event as any, newValue);
        }}
        disabled={multiline ? true : undefined}
        startAdornment={
          <AdornmentSelect
            variant="standard"
            size="small"
            value={selected}
            renderValue={(value) => {
              return (value as string).toUpperCase() || '';
            }}
            onChange={(event) => {
              setSelected(event.target.value as SupportedLanguageCode);
            }}
          >
            {SUPPORTED_LANGUAGE_CODES.map((locale) => (
              <MenuItem key={locale} value={locale}>
                {t(locale)}
              </MenuItem>
            ))}
          </AdornmentSelect>
        }
        endAdornment={
          isProcessingProps ? (
            <rootProps.slots.loadIcon />
          ) : multiline ? (
            <InputAdornment position="end">
              <IconButton
                onClick={() => {
                  setModalOpen(true);
                }}
              >
                {<EditNote fontSize="small" />}
              </IconButton>
            </InputAdornment>
          ) : undefined
        }
        {...other}
      />
      {multiline && (
        <I18nEditDialog
          key={selected}
          languageCode={selected}
          value={valueState}
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
          }}
          onChange={(newValue) => {
            handleChange({} as any, newValue);
          }}
        />
      )}
    </>
  );
});
